// Copyright (c) Docugami, Inc. All rights reserved.

import { homeGray } from "components/shell/presentation/AppStyles";
import { TextField } from "@fluentui/react";
import { CommandButton, IconAlignment } from "components/cards/CommandButton";
import { dgFontFamily, dgTagFontFamily } from "components/common/Fonts";
import { ConfirmationDialog, ConfirmationDialogProps } from "components/shell/utils/ConfirmationDialog";
import { useMemo, useState } from "react";
import { useSaveClauseCommand } from "hooks/SaveClauseCommand";
import { getAddClauseToLibraryCommand } from "hooks/ClauseCommands";
import { selectExistingClauseLabels } from "store/assistedauthinfo/selectors";
import styled from "styled-components";
import { useAppSelector } from "hooks/redux";
import { toPascalCase } from "utils/StringUtils";

const PlaceholderTextContainer = styled.span`
    font-family: ${dgFontFamily};
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    letter-spacing: 0em;
    text-align: center;
    color: ${homeGray};
`;

const placeholderText = "You can highlight any chunk of text and add it as a clause to your library."
export const ClauseLibraryPanePlaceholder = (): JSX.Element => {
    return <PlaceholderTextContainer>{placeholderText}</PlaceholderTextContainer>
}

export const AddClauseCommandButton = (props: { documentId: string, docSetId: string }): JSX.Element => {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const existingLabels = useAppSelector(state => selectExistingClauseLabels(state, props.docSetId));
    const { label, setLabel, isValidContent: addClauseButtonEnabled, canSaveClause: saveClauseEnabled, saveClause, disabledMessage } = useSaveClauseCommand(props.documentId);

    function onSaveClause() {
        if (!saveClauseEnabled) {
            return;
        }

        saveClause();
        clearSaveClauseState();
    }

    function clearSaveClauseState() {
        setShowConfirmationModal(false);
        setLabel('');
    }

    const command = getAddClauseToLibraryCommand({
        canExecute: () => addClauseButtonEnabled,
        execute: async () => setShowConfirmationModal(true),
        tooltip: disabledMessage
    });

    return (
        <div>
            <CommandButton
                command={command}
                dataAutomationId={toPascalCase(command.label, '')}
                width={13}
                height={18}
                iconAlignment={IconAlignment.Left} />
            <ClauseLabelModal
                label={label}
                setLabel={setLabel}
                existingLabels={existingLabels}
                hidden={!showConfirmationModal}
                confirmedCallback={onSaveClause}
                disabled={!saveClauseEnabled}
                dismissedCallback={clearSaveClauseState} />
        </div>
    )
}

const ModalContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

const ModalText = styled.span`
    font-family: Montserrat;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: center;
    width: 70%;
`;

const InfoText = styled(ModalText)`
    font-size: 12px;
    line-height: 14px;
    width: 100%;
`;

interface ClauseLabelModalProps extends Partial<ConfirmationDialogProps> {
    label: string;
    setLabel: (val: string) => void;
    existingLabels?: string[];
}

const ClauseLabelModal = (props: ClauseLabelModalProps): JSX.Element => {
    const showExistingLabelText = useMemo(() => props.existingLabels?.includes(props.label.toLocaleLowerCase()), [props.label, props.existingLabels]);

    return (
        <ConfirmationDialog
            hidden={props.hidden}
            confirmButtonText="OK"
            dismissButtonText="Cancel"
            confirmedCallback={props.confirmedCallback!}
            disabled={props.disabled}
            dismissedCallback={props.dismissedCallback!}>
            <ModalContentContainer>
                <ModalText>Give your clause a name and click OK.</ModalText>
                <TextField
                    value={props.label}
                    onChange={(ev, value) => props.setLabel(value ?? '')}
                    styles={{
                        root: {
                            width: '80%'
                        },
                        field: {
                            fontWeight: 700,
                            fontSize: '20px'
                        },
                        fieldGroup: {
                            fontFamily: dgTagFontFamily
                        }
                    }} />
                {!!showExistingLabelText && <InfoText>There is already a clause named <b>{props.label}</b> in your library. Click OK to add this as a variation, or give this a different name.</InfoText>}
            </ModalContentContainer>
        </ConfirmationDialog>
    )
}
